import React from 'react';
import { StepContentProps } from '../../models/ModelInvestments';

const StepContent: React.FC<StepContentProps> = ({
  currentStep,
  formData,
  showCustomTicker,
  handleInputChange,
  handleSelectOption,
  isSubmitting,
}): JSX.Element => {
  const inputClasses = "w-full p-3 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300";
  const buttonClasses = "w-full px-6 py-3 rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400";

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-6">Operation Type</h2>
            <div className="grid grid-cols-2 gap-4">
              {['buy', 'sell'].map((type) => (
                <button
                  key={type}
                  onClick={() => handleSelectOption('operationType', type)}
                  className={`${buttonClasses} ${
                    formData.operationType === type 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-6">Currency</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'brl', label: 'Real (BRL)' },
                { value: 'usd', label: 'Dollar (USD)' },
              ].map((currency) => (
                <button
                  key={currency.value}
                  onClick={() => handleSelectOption('currency', currency.value)}
                  className={`${buttonClasses} ${
                    formData.currency === currency.value 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                  }`}
                >
                  {currency.label}
                </button>
              ))}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-6">Operation Date</h2>
            <input
              type="date"
              name="operationDate"
              value={formData.operationDate || ''}
              onChange={handleInputChange}
              className={inputClasses}
            />
          </div>
        );
      case 4:
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-6">Digital Coin</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'BTC', label: 'Bitcoin' },
                { value: 'ETH', label: 'Ethereum' },
                { value: 'other', label: 'Other' }
              ].map((coin) => (
                <button
                  key={coin.value}
                  onClick={() => handleSelectOption('ticker', coin.value)}
                  className={`${buttonClasses} ${
                    formData.ticker === coin.value && !showCustomTicker
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-100'
                  }`}
                >
                  {coin.label}
                </button>
              ))}
            </div>
            {showCustomTicker && (
              <input
                type="text"
                name="ticker"
                value={formData.ticker || ''}
                onChange={handleInputChange}
                className="mt-4 w-full p-3 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Enter coin ticker"
              />
            )}
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold mb-4">Enter Volume</h2>
              <input
                type="number"
                name="volume"
                value={formData.volume || ''}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder="Enter volume"
                step="0.01"
              />
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-4">Enter Price</h2>
              <input
                type="number"
                name="price"
                value={formData.price || ''}
                onChange={handleInputChange}
                className={inputClasses}
                placeholder="Enter price"
                step="0.01"
              />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="w-full">
            <h2 className="text-2xl font-semibold mb-6 text-white">Review Your Selections</h2>
            <div className="bg-gray-900/50 rounded-lg p-8 border border-gray-700/50 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-gray-400 text-sm mb-1">Operation Type</div>
                  <div className="text-white font-medium">{formData.operationType}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Currency</div>
                  <div className="text-white font-medium">{formData.currency}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Operation Date</div>
                  <div className="text-white font-medium">{formData.operationDate}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Digital Coin</div>
                  <div className="text-white font-medium">{formData.ticker}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Volume</div>
                  <div className="text-white font-medium">{formData.volume}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-1">Price</div>
                  <div className="text-white font-medium">{formData.price}</div>
                </div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-xl font-semibold mb-6 text-white">Are these selections correct?</h3>
              <div className="flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => handleSelectOption('confirm', 'no')}
                  className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors"
                >
                  No, Go Back
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectOption('confirm', 'yes')}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-500 transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Yes, Confirm'}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="text-white">
      {renderStepContent()}
    </div>
  );
};

export default StepContent; 